/*
 * Soft Skin Quiz engine.
 *
 * Pure logic for the quiz at templates/page.soft-skin-quiz.json: the
 * question order, which six bars match the scent notes a shopper likes, and
 * how those six bars are written into (and read back from) the offer link.
 *
 * No DOM, no network, no randomness. The same answers, catalog, and config
 * always return the same six ordered slots. The component in
 * component-soft-skin-quiz.js owns rendering and storage.
 *
 * Loaded as a classic deferred script (window.CheekySoftSkinQuiz) and by the
 * node:test story suite through a vm sandbox.
 */
(function (root) {
  'use strict';

  var SCHEMA_VERSION = '1';
  var SLOT_COUNT = 6;

  /* Single-choice questions and their answer IDs. */
  var QUESTIONS = {
    skin_goal: ['tight', 'rough', 'later', 'softer'],
    current_cleanser: ['body_wash', 'regular_bar', 'combo', 'whatever'],
    switching_concern: ['dryness', 'lather', 'scent', 'longevity', 'value', 'none']
  };

  /*
   * scent_notes is the one multi-select question. Its options are scent
   * notes taken from the bars' fragrance descriptions and come from the
   * section config, so the engine only checks their shape here.
   */
  var MULTI_QUESTION = 'scent_notes';
  var NOTE_PATTERN = /^[a-z0-9_]{1,40}$/;
  var MAX_NOTES = 20;

  var PATH = ['skin_goal', 'current_cleanser', 'evidence', 'switching_concern', 'scent_notes'];

  var STAGES = [
    { id: 'routine', steps: ['skin_goal', 'current_cleanser', 'evidence'] },
    { id: 'preferences', steps: ['switching_concern', 'scent_notes'] },
    { id: 'bars', steps: ['results', 'unfulfillable'] }
  ];

  function has(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  /* Keep only known question IDs with known answer IDs. */
  function normalizeAnswers(raw) {
    var clean = {};
    if (!raw || typeof raw !== 'object') return clean;
    Object.keys(QUESTIONS).forEach(function (question) {
      var value = raw[question];
      if (typeof value === 'string' && QUESTIONS[question].indexOf(value) !== -1) {
        clean[question] = value;
      }
    });
    if (Array.isArray(raw[MULTI_QUESTION])) {
      var notes = [];
      raw[MULTI_QUESTION].forEach(function (note) {
        if (typeof note === 'string' && NOTE_PATTERN.test(note) && notes.indexOf(note) === -1 && notes.length < MAX_NOTES) {
          notes.push(note);
        }
      });
      if (notes.length) clean[MULTI_QUESTION] = notes;
    }
    return clean;
  }

  /* Ordered steps. The proof screen is a step, not a question. */
  function getPath() {
    return PATH.slice();
  }

  function isQuestion(step) {
    return has(QUESTIONS, step) || step === MULTI_QUESTION;
  }

  function isMultiSelect(step) {
    return step === MULTI_QUESTION;
  }

  function isComplete(answers) {
    var a = normalizeAnswers(answers);
    return PATH.every(function (step) {
      return !isQuestion(step) || has(a, step);
    });
  }

  /* Step after `step`, or 'results' once the path is finished. */
  function nextStep(answers, step) {
    var index = PATH.indexOf(step);
    if (index === -1) return PATH[0];
    return index + 1 < PATH.length ? PATH[index + 1] : 'results';
  }

  /* Step before `step`, or 'landing' from the first question. */
  function previousStep(answers, step) {
    if (step === 'results' || step === 'unfulfillable') return PATH[PATH.length - 1];
    var index = PATH.indexOf(step);
    return index > 0 ? PATH[index - 1] : 'landing';
  }

  function stageFor(step) {
    for (var i = 0; i < STAGES.length; i += 1) {
      if (STAGES[i].steps.indexOf(step) !== -1) return STAGES[i].id;
    }
    return null;
  }

  function findProduct(catalog, handle) {
    for (var i = 0; i < catalog.length; i += 1) {
      if (catalog[i] && catalog[i].handle === handle) return catalog[i];
    }
    return null;
  }

  /*
   * A bar can be auto-recommended when it is in stock, sits at the base
   * price, has a usable variant ID, and has enough units for the quantity
   * asked of it. `max` is null when Shopify does not cap the quantity.
   */
  function canSupply(product, quantity, config) {
    if (!product || product.available !== true) return false;
    if (!/^[1-9][0-9]{0,19}$/.test(String(product.variantId || ''))) return false;
    var base = config && config.basePriceCents;
    if (typeof base === 'number' && base > 0 && product.priceCents > base) return false;
    if (typeof product.max === 'number' && product.max < quantity) return false;
    return true;
  }

  function aggregate(products) {
    var items = [];
    products.forEach(function (product) {
      for (var i = 0; i < items.length; i += 1) {
        if (items[i].variantId === String(product.variantId)) {
          items[i].quantity += 1;
          return;
        }
      }
      items.push({
        handle: product.handle,
        variantId: String(product.variantId),
        title: product.title,
        quantity: 1
      });
    });
    return items;
  }

  /*
   * Returns either
   *   { status: 'ok', slots, items, recipe: 'notes', pickedNotes,
   *     missedNotes, ruleVersion }
   * or
   *   { status: 'unfulfillable', reason, missedNotes }
   *
   * The six slots are dealt one at a time to the picked scents in turn, so
   * each scent the shopper likes gets a fair share. The first round only
   * hands out bars that are not in the bundle yet, which keeps variety high. A scent's turn goes to
   * whichever of its bars has the fewest slots so far; ties go to the bar
   * that matches more picked scents, then to the order in the notes config.
   * One scent with one bar gives six of it. Two such scents give three each.
   */
  function recommend(answers, catalog, config) {
    var a = normalizeAnswers(answers);
    var list = Array.isArray(catalog) ? catalog : [];
    var settings = config || {};
    var ruleVersion = String(settings.ruleVersion || '1');
    var knownNotes = (settings.notes || []).map(function (note) { return note.id; });

    if (!isComplete(a)) {
      return { status: 'unfulfillable', reason: 'incomplete', missedNotes: [] };
    }

    var picked = a[MULTI_QUESTION].filter(function (note) { return knownNotes.indexOf(note) !== -1; });
    if (!picked.length) {
      return { status: 'unfulfillable', reason: 'incomplete', missedNotes: [] };
    }

    /* A picked note whose bars are all unavailable is reported, never hidden. */
    var missedNotes = (settings.notes || []).filter(function (note) {
      if (picked.indexOf(note.id) === -1) return false;
      return !(note.handles || []).some(function (handle) {
        return canSupply(findProduct(list, handle), 1, settings);
      });
    }).map(function (note) { return note.id; });

    var pickedNotes = (settings.notes || []).filter(function (note) { return picked.indexOf(note.id) !== -1; });
    var scores = {};
    pickedNotes.forEach(function (note) {
      (note.handles || []).forEach(function (handle) { scores[handle] = (scores[handle] || 0) + 1; });
    });

    var counts = {};
    var order = [];
    var filled = 0;
    var progressed = true;
    /* The first round only hands out bars not in the bundle yet, for variety. */
    var firstRound = true;
    while (filled < SLOT_COUNT && (progressed || firstRound)) {
      var newBarsOnly = firstRound;
      firstRound = false;
      progressed = false;
      for (var n = 0; n < pickedNotes.length && filled < SLOT_COUNT; n += 1) {
        var best = null;
        (pickedNotes[n].handles || []).forEach(function (handle, position) {
          var product = findProduct(list, handle);
          var assigned = counts[handle] || 0;
          if (newBarsOnly && assigned > 0) return;
          if (!canSupply(product, assigned + 1, settings)) return;
          var candidate = { product: product, assigned: assigned, score: scores[handle], position: position };
          if (!best
            || candidate.assigned < best.assigned
            || (candidate.assigned === best.assigned && candidate.score > best.score)) {
            best = candidate;
          }
        });
        if (!best) continue;
        var chosen = best.product.handle;
        if (!counts[chosen]) order.push(best.product);
        counts[chosen] = (counts[chosen] || 0) + 1;
        filled += 1;
        progressed = true;
      }
    }

    if (filled < SLOT_COUNT) {
      return { status: 'unfulfillable', reason: 'notes_short', missedNotes: missedNotes };
    }

    /* Slots are grouped by bar, in the order each bar first earned a slot. */
    var products = [];
    order.forEach(function (product) {
      for (var k = 0; k < counts[product.handle]; k += 1) products.push(product);
    });

    return {
      status: 'ok',
      slots: products.map(function (p) { return String(p.variantId); }),
      items: aggregate(products),
      recipe: 'notes',
      pickedNotes: picked,
      missedNotes: missedNotes,
      ruleVersion: ruleVersion
    };
  }

  var ID_PATTERN = /^[1-9][0-9]{0,19}$/;
  var RULE_PATTERN = /^[1-9][0-9]{0,5}$/;

  function validSlots(slots) {
    return Array.isArray(slots) && slots.length === SLOT_COUNT && slots.every(function (id) {
      return typeof id === 'string' && ID_PATTERN.test(id);
    });
  }

  /* Query string (no leading "?") carrying exactly the six displayed slots. */
  function serializeSelection(slots, ruleVersion, status) {
    if (!validSlots(slots)) throw new Error('soft-skin-quiz: a selection needs six variant ID strings');
    var state = status === 'custom' ? 'custom' : 'recommended';
    var rule = String(ruleVersion || '');
    if (state === 'recommended' && !RULE_PATTERN.test(rule)) {
      throw new Error('soft-skin-quiz: a recommended selection needs a rule version');
    }
    var params = new URLSearchParams();
    params.set('qv', SCHEMA_VERSION);
    if (RULE_PATTERN.test(rule)) params.set('rv', rule);
    params.set('qb', slots.join(','));
    params.set('qs', state);
    return params.toString();
  }

  /*
   * Append the selection to the configured offer address. Existing selection
   * keys on that address are replaced; any other parameters are kept.
   */
  function buildOfferUrl(offerUrl, slots, ruleVersion, origin) {
    var base = origin || 'https://usecheeky.com';
    var url = new URL(offerUrl, base);
    ['qv', 'rv', 'qb', 'qs'].forEach(function (key) { url.searchParams.delete(key); });
    var selection = new URLSearchParams(serializeSelection(slots, ruleVersion, 'recommended'));
    selection.forEach(function (value, key) { url.searchParams.set(key, value); });
    var sameOrigin = url.origin === new URL(base).origin;
    return sameOrigin ? url.pathname + url.search + url.hash : url.toString();
  }

  /*
   * Read a selection back from a query string. Returns { status: 'absent' }
   * when no selection keys are present, { status: 'invalid', reason } when
   * they are present but unusable, or { status: 'ok', slots, ruleVersion,
   * selectionStatus }. Catalog and stock checks are the caller's job.
   */
  function parseSelection(search) {
    var params = new URLSearchParams(search || '');
    var keys = ['qv', 'rv', 'qb', 'qs'];
    var present = keys.filter(function (key) { return params.has(key); });
    if (!present.length) return { status: 'absent' };
    for (var i = 0; i < keys.length; i += 1) {
      if (params.getAll(keys[i]).length > 1) return { status: 'invalid', reason: 'duplicate_key' };
    }
    if (params.get('qv') !== SCHEMA_VERSION) return { status: 'invalid', reason: 'unsupported_version' };
    var qs = params.get('qs');
    if (qs !== 'recommended' && qs !== 'custom') return { status: 'invalid', reason: 'bad_status' };
    var rv = params.get('rv');
    if (rv !== null && !RULE_PATTERN.test(rv)) return { status: 'invalid', reason: 'bad_rule_version' };
    if (qs === 'recommended' && rv === null) return { status: 'invalid', reason: 'missing_rule_version' };
    var qb = params.get('qb');
    if (typeof qb !== 'string' || qb.length > 125) return { status: 'invalid', reason: 'bad_selection' };
    var slots = qb.split(',');
    if (!validSlots(slots)) return { status: 'invalid', reason: 'bad_selection' };
    return { status: 'ok', slots: slots, ruleVersion: rv, selectionStatus: qs };
  }

  /* Order-independent "id:qty" summary for first-party analytics. */
  function signature(slots) {
    var counts = {};
    (slots || []).forEach(function (id) { counts[id] = (counts[id] || 0) + 1; });
    return Object.keys(counts).sort().map(function (id) { return id + ':' + counts[id]; }).join('|');
  }

  var api = {
    SCHEMA_VERSION: SCHEMA_VERSION,
    SLOT_COUNT: SLOT_COUNT,
    QUESTIONS: QUESTIONS,
    STAGES: STAGES,
    normalizeAnswers: normalizeAnswers,
    getPath: getPath,
    isQuestion: isQuestion,
    isMultiSelect: isMultiSelect,
    isComplete: isComplete,
    nextStep: nextStep,
    previousStep: previousStep,
    stageFor: stageFor,
    canSupply: canSupply,
    recommend: recommend,
    serializeSelection: serializeSelection,
    buildOfferUrl: buildOfferUrl,
    parseSelection: parseSelection,
    signature: signature
  };

  root.CheekySoftSkinQuiz = api;
})(typeof window !== 'undefined' ? window : globalThis);
